import { apiService } from '../../services/api';
import type { MappingResponse } from '../../services/api';

/**
 * Phase 5 (Task 3D-7): Fetch generated SQL from the backend.
 *
 * The backend uses sqlglot + SqlQueryBuilder.compose_target_query() to produce
 * correct, dialect-aware SQL. This replaces any client-side SQL generation
 * which would be fragile and not handle edge cases properly.
 *
 * Falls back to a simple placeholder if the backend endpoint is not available.
 */
export async function fetchGeneratedSql(
  recipeId: string,
  dialect: 'duckdb' | 'postgres' | 'snowflake' = 'duckdb'
): Promise<Record<string, string>> {
  try {
    const response = await apiService.getGeneratedSql(recipeId, dialect);
    return response;
  } catch (err) {
    // Fallback: If the endpoint doesn't exist yet, generate a placeholder
    console.warn('[generateSql] Backend endpoint not available, returning placeholder', err);
    return {};
  }
}

/**
 * Lightweight client-side SQL preview (used ONLY as fallback when backend is unreachable).
 * This is NOT dialect-aware — the real SQL generation happens server-side via sqlglot.
 */
export function generateSqlPreview(
  response: MappingResponse | null | undefined,
): Record<string, string> {
  if (!response || !Array.isArray(response.mappings) || !response.mappings.length) return {};

  // Group mappings by target table
  const byTarget = new Map<string, typeof response.mappings>();
  response.mappings.forEach(m => {
    const table = (m.target_table ?? '').trim();
    if (!table) return;
    if (!byTarget.has(table)) byTarget.set(table, []);
    byTarget.get(table)!.push(m);
  });

  const result: Record<string, string> = {};

  byTarget.forEach((mappings, targetTable) => {
    const lines: string[] = [
      `-- Target: ${targetTable}`,
      `-- ${mappings.length} mapping(s) — preview only, actual SQL generated server-side via sqlglot`,
      '',
      `INSERT INTO "${targetTable}"`,
      'SELECT',
    ];

    const columns = mappings.map(m => {
      const src = m.source_field ? `"${m.source_field}"` : 'NULL';
      const tgt = `"${m.target_field}"`;
      const expr = m.transformation && m.transformation_type !== 'direct'
        ? `${m.transformation} AS ${tgt}`
        : `${src} AS ${tgt}`;
      return `  ${expr}`;
    });

    lines.push(columns.join(',\n'));

    const sources = Array.from(new Set(mappings.map(m => m.source_file).filter(Boolean)));
    if (sources.length > 0) {
      lines.push(`FROM "${sources[0]}"`);
    }

    lines.push(';');
    result[targetTable] = lines.join('\n');
  });

  return result;
}
